import { Koi } from '@ksyos/koi';
import type { Section } from '@common/formEngine/formEngine.types';
import { Expert } from '@common/customControls/bariatrics';
import { dependsOn } from '@common/formEngine/dependsOn';
import { getTextAreaStringSchema } from '@common/validation/utils/getTextAreaStringSchema';
import { getTextInputStringSchema } from '@common/validation/utils/getTextInputStringSchema';

const bariPostMdoReportExpertsIntern = [
    Expert.lifestylecoach,
    Expert.psycholoog,
    Expert.dietist,
    Expert.medisch_specialist,
];

export type BariPostMdoReportExpertIntern = (typeof bariPostMdoReportExpertsIntern)[number];

export type BariPostMdoReportData = {
    beleid: string | null;
    consult_extern: boolean | null;
    consult_intern: boolean | null;
    datum_mdo: string | null;
    expert_extern: string | null;
    expert_intern: BariPostMdoReportExpertIntern | null;
    expert_intern_hulpvraag: string | null;
    verslag_mdo: string | null;
    verwijsbrief_nodig: boolean | null;
};

export const bariPostMdoReportSections: Section<BariPostMdoReportData>[] = [
    {
        fields: [
            {
                draftSchema: Koi.koi().todayOrBefore().allow(null),
                finalSchema: Koi.koi().todayOrBefore(),
                isRequired: true,
                key: 'datum_mdo',
                label: 'Datum MDO',
                maxValue: new Date(),
                type: 'dateInput',
            },
            {
                isRequired: true,
                key: 'verslag_mdo',
                label: 'Verslag MDO',
                type: 'textArea',
            },
        ],
    },
    {
        fields: [
            {
                isRequired: false,
                key: 'beleid',
                label: 'Beleid',
                type: 'textArea',
            },
            {
                isRequired: true,
                key: 'consult_intern',
                label: 'Kort intern consult',
                tooltipText:
                    'Deze consulten duren maximaal 15 minuten. Kies bijvoorbeeld deze optie als er een inschatting moet worden gemaakt of een doorverwijzing nodig is.',
                type: 'boolean',
            },
            {
                draftSchema: Koi.when('consult_intern', {
                    is: true,
                    otherwise: Koi.valid(null),
                    then: Koi.koi()
                        .valid(...bariPostMdoReportExpertsIntern)
                        .allow(null),
                }),
                finalSchema: Koi.when('consult_intern', {
                    is: true,
                    otherwise: Koi.valid(null),
                    then: Koi.koi().valid(...bariPostMdoReportExpertsIntern),
                }),
                include: dependsOn('consult_intern'),
                isRequired: true,
                key: 'expert_intern',
                label: 'Expert',
                type: 'radioGroup',
                valueOptions: bariPostMdoReportExpertsIntern,
            },
            {
                draftSchema: Koi.when('consult_intern', {
                    is: true,
                    otherwise: Koi.valid(null),
                    then: getTextAreaStringSchema(true),
                }),
                finalSchema: Koi.when('consult_intern', {
                    is: true,
                    otherwise: Koi.valid(null),
                    then: getTextAreaStringSchema(),
                }),
                include: dependsOn('consult_intern'),
                isRequired: true,
                key: 'expert_intern_hulpvraag',
                label: 'Hulpvraag aan expert',
                type: 'textArea',
            },
            {
                isRequired: true,
                key: 'consult_extern',
                label: 'Extern consult',
                tooltipText:
                    'Externe consulten kies je voor alle zorg die langer dan 15 minuten duurt en/of als je een expert nodig hebt die niet bij de interne opties staat.',
                type: 'boolean',
            },
            {
                draftSchema: Koi.when('consult_extern', {
                    is: true,
                    otherwise: Koi.valid(null),
                    then: getTextInputStringSchema(true),
                }),
                finalSchema: Koi.when('consult_extern', {
                    is: true,
                    otherwise: Koi.valid(null),
                    then: getTextInputStringSchema(true),
                }),
                include: dependsOn('consult_extern'),
                key: 'expert_extern',
                label: 'Type externe expert',
                type: 'textInput',
            },
            {
                draftSchema: Koi.when('consult_extern', {
                    is: true,
                    otherwise: Koi.valid(null),
                    then: Koi.boolean().allow(null),
                }),
                finalSchema: Koi.when('consult_extern', {
                    is: true,
                    otherwise: Koi.valid(null),
                    then: Koi.boolean(),
                }),
                include: dependsOn('consult_extern'),
                isRequired: true,
                key: 'verwijsbrief_nodig',
                label: 'Verwijsbrief nodig',
                type: 'boolean',
            },
        ],
        title: 'Conclusie',
    },
];
